import { useCallback, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  getCarMakers,
  getModelLines,
  getCarYears,
  getCarModifications,
} from "../car.services";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";

// Types
interface CarItem {
  id: number;
  name: string;
  value: string;
  label: string;
  isPopular?: boolean;
}

interface CarMaker extends CarItem {}
interface ModelLine extends CarItem {}
interface CarYear {
  value: string;
  label: string;
}
interface CarModification extends CarItem {}

interface CarConfiguration {
  id: number;
  name: string;
  yearStart: number;
  yearEnd?: number;
  fuelType?: string;
  engineType?: string;
  emissionNorms?: string;
}

// Popular IDs used to sort dropdowns
const popularCarMakers = [286, 212, 278, 429];
const popularCarModelLines: Record<number, number[]> = {
  286: [11299, 11292, 11290, 11921, 11302, 12484],
  212: [11253, 11247, 12483, 11248, 12325, 11244],
  278: [11280, 11281, 12428, 12315, 11275, 11283],
  429: [12240, 12437, 12017, 12357, 12183, 12066],
};

// Common utility
const sortWithPopularsFirst = (
  items: CarItem[],
  popularIds: number[]
): CarItem[] => {
  const populars: CarItem[] = [];
  const others: CarItem[] = [];

  items.forEach((item) => {
    if (popularIds.includes(parseInt(item.value))) {
      populars.push({ ...item, isPopular: true, label: `${item.label} ⭐` });
    } else {
      others.push(item);
    }
  });

  populars.sort((a, b) => {
    return (
      popularIds.indexOf(parseInt(a.value)) -
      popularIds.indexOf(parseInt(b.value))
    );
  });

  return [...populars, ...others];
};

export const useCarMakers = (onError?: (message: string) => void) => {
  const {
    data,
    isLoading,
    error
  } = useInfiniteQuery({
    queryKey: ["carMakers"],
    queryFn: async () => {
      const makers = await getCarMakers();
      const transformed = makers.map((maker: any) => ({
        ...maker,
        value: maker.id.toString(),
        label: maker.name,
      }));
      return sortWithPopularsFirst(transformed, popularCarMakers);
    },
    staleTime: Infinity,
    onError: () => {
      onError?.("Failed to load car makers. Please try again.");
    },
  });

  const carMakers = useMemo(() => {
    return data?.pages[0] || [];
  }, [data]);

  return {
    carMakers,
    loading: isLoading,
    error
  };
};

export const useModelLines = (
  carMakerId: string | null,
  onError?: (message: string) => void
) => {
  const {
    data,
    isLoading,
    error
  } = useInfiniteQuery({
    queryKey: ["modelLines", carMakerId],
    queryFn: async () => {
      if (!carMakerId) return [];

      const models = await getModelLines(carMakerId);
      const transformed = models.map((model: any) => ({
        ...model,
        value: model.id.toString(),
        label: model.name,
      }));

      const popular = popularCarModelLines[parseInt(carMakerId)] || [];
      return sortWithPopularsFirst(transformed, popular);
    },
    enabled: !!carMakerId,
    staleTime: Infinity,
    onError: () => {
      onError?.("Failed to load model lines. Please try again.");
    },
  });

  const modelLines = useMemo(() => {
    return data?.pages[0] || [];
  }, [data]);

  return {
    modelLines,
    loading: isLoading,
    error
  };
};

export const useYears = (
  modelLineId: string | null,
  carMakerId: string | null,
  onError?: (message: string) => void
) => {
  const {
    data,
    isLoading,
    error
  } = useInfiniteQuery({
    queryKey: ["carYears", modelLineId, carMakerId],
    queryFn: async () => {
      if (!modelLineId) return [];

      const configs = await getCarYears(modelLineId, carMakerId || "");

      const currentYear = new Date().getFullYear();
      const yearSet = new Set<number>();

      configs.forEach((config: CarConfiguration) => {
        const start = config.yearStart;
        const end = config.yearEnd || currentYear;
        for (let y = start; y <= end; y++) yearSet.add(y);
      });

      const uniqueYears = Array.from(yearSet).sort(
        (a: number, b: number) => a - b
      );
      return uniqueYears.map((y) => ({
        value: y.toString(),
        label: y.toString(),
      }));
    },
    enabled: !!modelLineId,
    staleTime: Infinity,
    onError: () => {
      onError?.("Failed to load years. Please try again.");
    },
  });

  const years = useMemo(() => {
    return data?.pages[0] || [];
  }, [data]);

  return {
    years,
    loading: isLoading,
    error
  };
};

export const useModifications = (
  modelLineId: string | null,
  year: string | null,
  carMakerId: string | null,
  onError?: (message: string) => void
) => {
  const {
    data,
    isLoading,
    error
  } = useInfiniteQuery({
    queryKey: ["carModifications", modelLineId, year, carMakerId],
    queryFn: async () => {
      if (!modelLineId || !year) return [];

      const configs = await getCarModifications(
        modelLineId,
        year,
        carMakerId || ""
      );
      const transformed = configs.map((config: any) => ({
        ...config,
        value: config.id.toString(),
        label: getModificationLabel(config),
      }));
      return transformed;
    },
    enabled: !!(modelLineId && year),
    staleTime: Infinity,
    onError: () => {
      onError?.("Failed to load modifications. Please try again.");
    },
  });

  const modifications = useMemo(() => {
    return data?.pages[0] || [];
  }, [data]);

  return {
    modifications,
    loading: isLoading,
    error
  };
};

const getModificationLabel = (modification: CarConfiguration): string => {
  let label = modification.name;
  if (modification.fuelType) {
    label += `/${modification.fuelType}`;
  }
  if (modification.engineType) {
    label += `/${modification.engineType}`;
  }
  if (modification.emissionNorms) {
    label += `/${modification.emissionNorms}`;
  }
  return label;
};

export const useLicensePlateNumberValidator = () => {
  const { data: mobileConfigurations } = useGetOrderMediaConfig();

  const licensePlateRegex: RegExp = useMemo(() => {
    const defaultRegex =
      /^([A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{4}|[0-9]{2}BH[0-9]{4}[A-Z]{1,2})$/;

    const patternFromConfig = mobileConfigurations?.license_plate_number_regex;
    if (!patternFromConfig) return defaultRegex;
    try {
      return new RegExp(patternFromConfig);
    } catch (error) {
      console.warn(
        "Invalid license plate regex pattern from config, using default:",
        error
      );
      return defaultRegex;
    }
  }, [mobileConfigurations?.license_plate_number_regex]);

  const validate = useCallback(
    (value: string) => {
      const cleanedInput = value
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, "");
      return cleanedInput === "" || licensePlateRegex.test(cleanedInput);
    },
    [licensePlateRegex]
  );

  return validate;
};
